/**
 * Helper para consumir a API de raspagem (/task)
 * BaseURL padrão pode ser configurada via REACT_APP_RASPAGEM_BASE_URL
 */

export const RASPAGEM_DEFAULT_BASE_URL =
  (process.env.REACT_APP_RASPAGEM_BASE_URL || "https://raspagem-hml.blops.com.br").replace(/\/$/, "")

function pad(value) {
  return String(value).padStart(2, "0")
}

function formatDateYmdHmsFromDate(date) {
  const year = date.getFullYear()
  const month = pad(date.getMonth() + 1)
  const day = pad(date.getDate())
  const hours = pad(date.getHours())
  const minutes = pad(date.getMinutes())
  const seconds = pad(date.getSeconds())
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
}

function normalizeDateTime(input) {
  if (!input) return null
  if (typeof input === "string") {
    let s = input.trim()
    // Converte ISO-like para "YYYY-MM-DD HH:MM:SS"
    s = s.replace("T", " ")
    s = s.replace(/Z$/, "")
    // Remove milissegundos, se houver
    if (s.includes(".")) s = s.split(".")[0]
    // Mantém apenas os 19 primeiros chars no formato esperado
    if (s.length >= 19) {
      s = s.slice(0, 19)
    }
    // Validação simples do formato final
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(s)) return s
    // Fallback: tentar date parsing
    const d = new Date(input)
    if (!Number.isNaN(d.getTime())) return formatDateYmdHmsFromDate(d)
    return null
  }
  if (input instanceof Date) {
    if (Number.isNaN(input.getTime())) return null
    return formatDateYmdHmsFromDate(input)
  }
  const d = new Date(input)
  if (Number.isNaN(d.getTime())) return null
  return formatDateYmdHmsFromDate(d)
}

/**
 * Lista tarefas executadas pelos robôs em um intervalo de tempo.
 * dt_min é recomendado; dt_max é opcional (quando run em andamento).
 * Retorna o objeto "data" da resposta quando disponível.
 */
export async function listTasks({ baseUrl = RASPAGEM_DEFAULT_BASE_URL, dt_min, dt_max, signal } = {}) {
  const min = normalizeDateTime(dt_min)
  const max = normalizeDateTime(dt_max)

  if (!min && !max) {
    throw new Error("Informe dt_min e/ou dt_max para consultar tarefas")
  }

  // Usar encodeURIComponent para garantir espaço como %20 (alguns servidores não tratam + como espaço)
  const query = `${min ? `dt_min=${encodeURIComponent(min)}` : ""}${min && max ? "&" : ""}${max ? `dt_max=${encodeURIComponent(max)}` : ""}`
  const url = `${baseUrl}/tasks?${query}`
  const res = await fetch(url, { method: "GET", headers: { Accept: "application/json" }, signal })
  if (!res.ok) {
    const text = await res.text().catch(() => "")
    throw new Error(`Raspagem /tasks falhou: HTTP ${res.status} ${text}`)
  }
  const json = await res.json()
  return json?.data ?? json
}

/**
 * Conta quantidade de tarefas por robô em um intervalo.
 * Retorna array de objetos: { Robo: string, Tarefas: number }
 */
export async function listTaskCounts({ baseUrl = RASPAGEM_DEFAULT_BASE_URL, dt_min, dt_max, signal } = {}) {
  const min = normalizeDateTime(dt_min)
  const max = normalizeDateTime(dt_max)

  const hasAny = Boolean(min || max)
  const query = hasAny ? `${min ? `dt_min=${encodeURIComponent(min)}` : ""}${min && max ? "&" : ""}${max ? `dt_max=${encodeURIComponent(max)}` : ""}` : ""
  const url = `${baseUrl}/tasks/count${hasAny ? `?${query}` : ""}`
  const res = await fetch(url, { method: "GET", headers: { Accept: "application/json" }, signal })
  if (!res.ok) {
    const text = await res.text().catch(() => "")
    throw new Error(`Raspagem /task/count falhou: HTTP ${res.status} ${text}`)
  }
  const json = await res.json()
  const data = Array.isArray(json?.data) ? json.data : []
  return data
}

/**
 * Mapeia statusTarefaID para rótulo e cor:
 * 1 = Em execução
 * 2 = Encontrou dados
 * 3 = Não encontrou nada, erro
 */
export function mapTaskStatus(statusId, conteudo) {
  const id = Number(statusId)
  if (id === 2 && (conteudo == null || String(conteudo).trim() === "")) {
    // status 2, mas ainda sem Conteudo: processamento pendente de saída
    return { label: "Ainda em processamento", colorClass: "text-primary" }
  }
  switch (id) {
    case 1:
      return { label: "Em execução", colorClass: "text-primary" }
    case 2:
      return { label: "Encontrou dados", colorClass: "text-success" }
    case 3:
      return { label: "Não encontrou nada, erro", colorClass: "text-danger" }
    default:
      return { label: "-", colorClass: "text-muted" }
  }
}

/**
 * Lista status dos robôs (histórico ou por run_id se fornecido)
 * GET /bots ou /bots?run_id=1170
 */
export async function listBots({ baseUrl = RASPAGEM_DEFAULT_BASE_URL, run_id, signal } = {}) {
  const hasRun = run_id != null && run_id !== ""
  const url = `${baseUrl}/bots${hasRun ? `?run_id=${encodeURIComponent(run_id)}` : ""}`
  const res = await fetch(url, { method: "GET", headers: { Accept: "application/json" }, signal })
  if (!res.ok) {
    const text = await res.text().catch(() => "")
    throw new Error(`Raspagem /bots falhou: HTTP ${res.status} ${text}`)
  }
  const json = await res.json()
  return Array.isArray(json?.data) ? json.data : []
}


